import type { ApiResponseBody } from '@/lib/api/api-type-helper';
import { featureFlags } from '@/lib/env';

export type ProductPermissionScopeCode = ApiResponseBody<
  '/v2/session',
  'get'
>['hasPermissionScopes'][number];

export const PRODUCT_PERMISSION_SCOPE_CODES = {
  // Marketplace
  MARKETPLACE: 'marketplace',
  // HR Force
  PEOPLE: 'people',
  ORGANIZATION: 'organization',
  ORGANIZATION_PLAN: 'organization-restructuring',
  PUBLIC_IDS: 'public-ids',
  CERTIFICATION: 'certification',
  LABOR_CORE: 'labor-core',
  CONTRACTOR_CORE: 'contractor-core',
  TIME_AND_ATTENDANCE: 'time-and-attendance',
  // IT Force
  // TODO: AppsからSoftwareに移行した際にUIは移行済だが、パーミッションの移行が現状できていない状態
  APPS: 'apps',
  // SOFTWARE: "software",
  ACCESSES: 'accesses',
  DEVICES: 'devices',
  ID_MANAGEMENT: 'id-management',
  LICENSES: 'licenses',
  STORAGE_FILES: 'storage-files',
  NETWORKS: 'networks',
  STARLINK: 'starlink',
  IT_OPERATION_CORE: 'it-operation-core',
  // GA Force
  PLACE: 'place',
  EQUIPMENT: 'equipment',
  GA_OPERATION_CORE: 'ga-operation-core',
  FLEET: 'fleet',
  // LI Force
  CONTRACT: 'contract',
  BUSINESS_PARTNER: 'business-partner',
  // PRC Force
  SUPPLIER_LEDGER: 'supplier-ledger',
  DEAL_LEDGER: 'deal-ledger',
  // Settings
  USER_ACCOUNTS: 'user-accounts',
  PERMISSION_AND_ROLE: 'permission-and-role',
  CONTRACTING_CORPORATION_PROFILE: 'contracting-corporation-profile',
  DATA_CONNECTOR: 'data-connector',

  /** External Notification */
  EXTERNAL_NOTIFICATION: 'external-notification',
  // BOE
  OPERATION_EVENT: 'operation-event',
  // Member Portal
  MY_PROFILE: 'my-profile',
} as const;

export const isProductPermissionScopeCode = (
  code: string,
): code is ProductPermissionScopeCode => {
  // NOTE: 型が追加された際もコンパイルで落ちないようにダウンキャストする
  const codes: string[] = Object.values(PRODUCT_PERMISSION_SCOPE_CODES);
  return Object.values(codes).includes(code as ProductPermissionScopeCode);
};

/**
 * 表示しないスコープのリスト
 * 画面が提供されておらず、画面に見えると誤解を招くため除外するスコープを定義
 */
const HIDDEN_PRODUCT_PERMISSION_SCOPE_CODES: readonly ProductPermissionScopeCode[] =
  [
    PRODUCT_PERMISSION_SCOPE_CODES.EXTERNAL_NOTIFICATION,
    // deal-ledger（取引台帳）は BE がスコープを追加済みだがフロントの画面が未提供のため非表示。
    // 画面提供時にこのエントリを削除する。
    PRODUCT_PERMISSION_SCOPE_CODES.DEAL_LEDGER,
  ] as const;

/**
 * フィーチャーフラグで制御されるスコープのマップ
 * key: スコープコード, value: フィーチャーフラグ名
 *
 * 新しいスコープをフィーチャーフラグで制御する場合は、このマップにエントリを追加してください
 */
const FEATURE_FLAG_CONTROLLED_SCOPES: Partial<
  Record<ProductPermissionScopeCode, Parameters<typeof featureFlags.check>[0]>
> = {
  // 例: [PRODUCT_PERMISSION_SCOPE_CODES.STARLINK]: "STARLINK",
  // PRC Force / 仕入先台帳は SUPPLIER_LEDGER フラグで制御（本番は flag=false で非表示）
  [PRODUCT_PERMISSION_SCOPE_CODES.SUPPLIER_LEDGER]: 'SUPPLIER_LEDGER',
};

/**
 * スコープが表示対象かどうかを判定する
 * @param scopeCode スコープコード
 * @returns 表示対象の場合 true
 */
export const isVisibleProductPermissionScope = (scopeCode: string): boolean => {
  // HIDDEN_PRODUCT_PERMISSION_SCOPE_CODES に含まれている場合は非表示
  if (
    (HIDDEN_PRODUCT_PERMISSION_SCOPE_CODES as readonly string[]).includes(
      scopeCode,
    )
  ) {
    return false;
  }

  // フィーチャーフラグによる制御
  const featureFlag =
    FEATURE_FLAG_CONTROLLED_SCOPES[scopeCode as ProductPermissionScopeCode];
  if (featureFlag) {
    return featureFlags.check(featureFlag);
  }

  return true;
};

/**
 * カテゴリ内のスコープを表示対象のみにフィルタリングする
 * @param category スコープを持つカテゴリオブジェクト
 * @returns フィルタリングされたカテゴリ
 */
export const filterVisibleScopes = <T extends { scopes: { code: string }[] }>(
  category: T,
): T => ({
  ...category,
  scopes: category.scopes.filter((s) =>
    isVisibleProductPermissionScope(s.code),
  ),
});
